import { Badge, Modal, Table } from "flowbite-react";
import {
  formatDateToDDMM,
  formatDateToYY,
  formatToReal,
  getCompanyAge,
  getImageURLScore,
  getLetterScore,
  getScoreLegends,
  isValidCNPJ,
  maskCEP,
  parseFoundationDate,
} from "../../utils/utils";
import TitleStripe from "../common/TitleStripe";
import NoOcurrence from "../common/query/NoOcurrence";
import Protestos from "../common/query/Protestos";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import ChequesSustados from "../common/query/ChequesSustados";
import PendenciasFinanceiras from "../common/query/PendenciasFinanceiras";
import AcoesCiveis from "../common/query/AcoesCiveis";
import FalenciaRecuperacaoJudicial from "../common/query/FalenciaRecuperacaoJudicial";
import QuadroSocietario from "../common/query/QuadroSocietario";
import HistoricoConsultas from "../common/query/HistoricoConsultas";
import AvisoCard from "../common/query/AvisoCard";
import Score from "../common/query/Score";
import Administrador from "../common/query/Administrador";
import ScoreRating from "./custom-queries-credit/ScoreRating";
import DecisionBanner from "./DecisionBanner";
import moment from "moment";
import StreetView from "../common/query/StreetView";
import { HiOutlineEye } from "react-icons/hi";
import { useState } from "react";
import ModalReceitaFederal from "../modals/ModalReceitaFederal";

/* `hideAgrupados`: na visão consolidada por tipo ("Ver tudo") este insumo entra
   normalizado como Fonte 1, então cadastro, score, quadro societário,
   administradores, participações, cheques, protestos, negativações, ações,
   falências e histórico já saem agrupados por tipo. Aqui ficam só as seções
   exclusivas: faturamento presumido, limite de crédito, alerta legal, crédito
   obtido, pagamento pontual, atrasos, compromissos e comprometimento futuro. */
const DefineLimitePositivoQuery = ({
  data,
  dataFull,
  hideAgrupados = false,
}) => {
  const probRaw = +data?.score?.score_pj?.probabilidade;
  const prob = typeof probRaw === "number" ? probRaw : null;
  const [openMap, setOpenMap] = useState(false);

  const fullAddress = [
    data?.localizacao_completo?.matriz?.endereco_matriz,
    data?.localizacao_completo?.matriz?.numero_matriz,
    data?.localizacao_completo?.matriz?.bairro_matriz,
    data?.localizacao_completo?.matriz?.cidade_matriz &&
    data?.localizacao_completo?.matriz?.uf_matriz
      ? `${data?.localizacao_completo?.matriz?.cidade_matriz} - ${data?.localizacao_completo?.matriz?.uf_matriz}`
      : data?.localizacao_completo?.matriz?.cidade_matriz ||
        data?.localizacao_completo?.matriz?.uf_matriz,
    maskCEP(data?.localizacao_completo?.matriz?.cep_matriz),
  ]
    .filter(Boolean)
    .join(", ");

  let riskLabel = "Indefinido";
  let riskClasses = {
    chip: "bg-gray-100 text-gray-700",
    ring: "from-slate-50 to-slate-100",
    dotActive: "bg-gray-300",
    dotIdle: "bg-gray-100",
  };

  if (prob !== null) {
    if (prob < 10) {
      riskLabel = "Baixo";
      riskClasses = {
        chip: "bg-green-100 text-green-700",
        ring: "from-green-50 to-teal-50",
        dotActive: "bg-green-500",
        dotIdle: "bg-green-100",
      };
    } else if (prob < 25) {
      riskLabel = "Moderado";
      riskClasses = {
        chip: "bg-amber-100 text-amber-700",
        ring: "from-amber-50 to-yellow-50",
        dotActive: "bg-amber-500",
        dotIdle: "bg-amber-100",
      };
    } else if (prob < 60) {
      riskLabel = "Alto";
      riskClasses = {
        chip: "bg-orange-100 text-orange-700",
        ring: "from-orange-50 to-amber-50",
        dotActive: "bg-orange-500",
        dotIdle: "bg-orange-100",
      };
    } else {
      riskLabel = "Altíssimo";
      riskClasses = {
        chip: "bg-rose-100 text-rose-700",
        ring: "from-rose-50 to-red-50",
        dotActive: "bg-rose-500",
        dotIdle: "bg-rose-100",
      };
    }
  }

  return (
    <div>
      <DecisionBanner compact={true} flag={data?.decisao?.flag} />

      {!hideAgrupados && (
        <>
          <TitleStripe>Dados Cadastrais</TitleStripe>

          <div className="mt-4 grid grid-cols-2 gap-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-sm">
            <div className="space-y-4">
              <div>
                <div className="text-gray-500 text-xs uppercase">Razão Social</div>
                <div className="font-medium text-gray-900">
                  {data?.identificacao_completo?.razao_social || "Indisponível"}
                </div>
                <ModalReceitaFederal cnpj={data?.identificacao_completo?.cnpj} />
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">Nome Fantasia</div>
                <div className="font-medium text-gray-900">
                  {data?.identificacao_completo?.nome_fantasia || "Indisponível"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">
                  Natureza Jurídica
                </div>
                <div className="font-medium text-gray-900">
                  {data?.identificacao_completo?.natureza_juridica ||
                    "Indisponível"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">CNPJ/MF</div>
                <div className="font-medium text-gray-900">
                  {data?.identificacao_completo?.cnpj || "Indisponível"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">
                  Inscrição Estadual
                </div>
                <div className="font-medium text-gray-900">
                  {data?.identificacao_completo?.inscricao_estadual ||
                    "Indisponível"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">Segmento</div>
                <div className="font-medium text-gray-900">
                  {data?.identificacao_completo?.segmento || "Indisponível"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">
                  CNAE Principal
                </div>
                <div className="font-medium text-gray-900">
                  {data?.identificacao_completo?.ramo_atividade_primario
                    ?.atividade || "Indisponível"}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-gray-500 text-xs uppercase">
                  Situação do CNPJ
                </div>
                <div
                  className={`inline-block px-2 py-1 text-xs font-semibold rounded-md text-white ${
                    data?.identificacao_completo?.situacao_cnpj?.toLowerCase() ===
                    "ativo"
                      ? "bg-green-600"
                      : "bg-red-600"
                  }`}
                >
                  {data?.identificacao_completo?.situacao_cnpj || "Indisponível"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">Endereço</div>
                <div className="font-medium text-gray-900">
                  {data?.localizacao_completo?.matriz?.endereco_matriz ||
                    "Indisponível"}
                  {fullAddress && (
                    <div
                      className="flex items-center text-xs gap-1 text-blue-600 cursor-pointer"
                      onClick={() => setOpenMap(true)}
                    >
                      <span>- Ver fotos do local</span>
                      <HiOutlineEye className="text-lg" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">Bairro</div>
                <div className="font-medium text-gray-900">
                  {data?.localizacao_completo?.matriz?.bairro_matriz ||
                    "Indisponível"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">Cidade/UF</div>
                <div className="font-medium text-gray-900">
                  {data?.localizacao_completo?.matriz?.cidade_matriz ||
                    "Indisponível"}{" "}
                  / {data?.localizacao_completo?.matriz?.uf_matriz || "--"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase">CEP</div>
                <div className="font-medium text-gray-900">
                  {maskCEP(data?.localizacao_completo?.matriz?.cep_matriz) ||
                    "Indisponível"}
                </div>
              </div>

              {(() => {
                const fundacaoStr = data?.identificacao_completo?.data_fundacao;
                const fundacao = parseFoundationDate(fundacaoStr);
                const age = getCompanyAge(fundacao);

                return (
                  <div>
                    <div className="text-gray-500 text-xs uppercase">Fundação</div>
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">
                        {fundacao ? fundacao.format("DD/MM/YYYY") : "Indisponível"}
                      </div>
                      <Badge className="text-xs text-gray-500 w-fit">
                        {fundacao && age
                          ? `${age.years} ano${age.years !== 1 ? "s" : ""}${
                              age.months
                                ? ` e ${age.months} mes${
                                    age.months !== 1 ? "es" : ""
                                  }`
                                : ""
                            }`
                          : "—"}
                      </Badge>
                    </div>
                  </div>
                );
              })()}

              <div>
                <div className="text-gray-500 text-xs uppercase">
                  Capital Social
                </div>
                <div className="font-medium text-gray-900">
                  {formatToReal(data?.identificacao_completo?.capital_inicial) ||
                    "Indisponível"}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <TitleStripe>Avaliação Preliminar de Crédito</TitleStripe>

      <div className="mt-4 bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-sm space-y-6">
        {/* Limite de Crédito */}
        <div className="grid grid-cols-2 gap-4 -mt-6">
          <div>
            <TitleStripe>💰 Faturamento Presumido</TitleStripe>
            <div className="relative rounded-xl overflow-hidden bg-white shadow-sm mt-3">
              {/* fundo sutil */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-orange-50" />
              <div className="relative p-4">
                <div className="text-xl font-extrabold text-gray-900">
                  {
                    // se tiver formatToReal e for número, formata. senão, usa o valor como veio
                    typeof data?.faturamento_presumido?.faturamento_anual ===
                    "number"
                      ? formatToReal(
                          data.faturamento_presumido.faturamento_anual
                        )
                      : data?.faturamento_presumido?.faturamento_anual ||
                        "Indisponível"
                  }
                </div>

                {data?.faturamento_presumido?.mensagem && (
                  <div
                    className="mt-2 text-[11px] text-gray-600 bg-white/60 rounded-lg p-2"
                    style={{ lineHeight: "1.1" }}
                  >
                    {data.faturamento_presumido.mensagem}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <TitleStripe>🏦 Limite de crédito</TitleStripe>
            <div className="relative rounded-2xl overflow-hidden bg-white shadow-sm mt-3">
              {/* fundo sutil */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50" />
              <div className="relative p-4">
                <div className="text-xl font-extrabold text-gray-900">
                  {data?.limite_credito?.score
                    ? formatToReal(data.limite_credito.score)
                    : "Indisponível"}
                </div>

                {/* linha auxiliar opcional */}
                <div className="mt-1 text-[12px] text-gray-500">
                  {data?.limite_credito?.score ? (
                    <div
                      className="mt-2 text-[11px] text-gray-600 bg-white/60 rounded-lg p-2"
                      style={{ lineHeight: "1.1" }}
                    >
                      Limite de crédito estimado a partir de informações
                      comportamentais e cadastrais da empresa.
                    </div>
                  ) : (
                    <div
                      className="mt-2 text-[11px] text-gray-600 bg-white/60 rounded-lg p-2"
                      style={{ lineHeight: "1.1" }}
                    >
                      Limite de crédito estimado a partir de informações
                      comportamentais e cadastrais da empresa.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {!hideAgrupados && (
          <>
            {/* Score de Risco */}
            <div>
              <TitleStripe>📊 Score de Risco</TitleStripe>
              <div className="flex  flex-row items-center gap-6">
                {+data?.score?.score_pj?.score !== 2 ? (
                  <>
                    <Score
                      score={+data?.score?.score_pj?.score}
                      className="flex flex-col items-center md:w-1/3"
                    />
                    <div className="w-2/3 relative rounded-2xl overflow-hidden shadow-sm border border-gray-200">
                      {/* fundo sutil */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${riskClasses.ring}`}
                      />

                      <div className="relative p-4">
                        {/* header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">📉</span>
                            <div className="text-sm font-semibold text-gray-700">
                              Probabilidade de Inadimplência
                            </div>
                          </div>

                          {/* badges à direita */}
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${riskClasses.chip}`}
                            >
                              {riskLabel}
                            </span>
                            <span className="text-sm px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-semibold">
                              {prob !== null ? `${prob}%` : "--"}
                            </span>
                          </div>
                        </div>

                        {/* escala de risco com 4 pontos (não é barra de progresso) */}
                        <div className="flex items-center gap-2 mb-3">
                          {[0, 1, 2, 3].map((i) => {
                            const thresholds = [10, 25, 50, 100];
                            const active =
                              prob !== null ? prob >= thresholds[i] : false;
                            return (
                              <div
                                key={i}
                                className={`h-2 w-full rounded-full transition-colors ${
                                  active
                                    ? riskClasses.dotActive
                                    : riskClasses.dotIdle
                                }`}
                              />
                            );
                          })}
                        </div>

                        {/* texto explicativo */}
                        <div className="text-sm text-gray-700 leading-relaxed bg-white/70 rounded-lg p-3">
                          {data?.score?.score_pj?.texto ? (
                            <>{data.score.score_pj.texto}</>
                          ) : (
                            <span className="italic text-gray-400">
                              Sem explicação adicional.
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
                    <p className="font-medium">
                      Não foi possível calcular o score desta empresa.
                    </p>
                    <p className="mt-1">
                      É necessário verificar as outras informações disponíveis no
                      relatório para uma análise completa.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {dataFull?.scoreRating?.statusRetorno == 1 && (
              <ScoreRating score={dataFull?.scoreRating} block={false} />
            )}
          </>
        )}

        {/* Alerta Legal */}
        <div className="text-xs text-gray-600 border-t pt-4">
          <span className="font-semibold text-gray-700">Importante:</span> a
          decisão de aprovação ou não do crédito é de exclusiva responsabilidade
          do concedente. As informações prestadas pelo provedor têm como
          objetivo subsidiar essas decisões e, em hipótese alguma, devem ser
          utilizadas como justificativa única para concessão ou recusa de
          crédito.
        </div>
      </div>

      {!hideAgrupados && (
        <>
          {data?.score?.score && +data?.score?.score != 2 && (
            <div className="rounded-2xl border border-gray-200">
              <div className="w-full  px-4 py-2 flex flex-row items-center justify-between gap-6 max-w-6xl mx-auto">
                {/* Coluna 1 - Nota + Descrição */}
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Imagem do score */}
                  <img
                    src={`/assets/score/rating/${getLetterScore(
                      +data?.score?.score
                    )}.png`}
                    alt="Nota Score"
                    className="w-32 shrink-0"
                  />

                  {/* Texto */}
                  <div className="text-sm text-gray-700 leading-snug">
                    <div className="font-semibold mb-1">Descritivo:</div>
                    <div className="text-gray-600 text-xs md:text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
                      {getScoreLegends(getLetterScore(+data?.score?.score))}
                    </div>
                  </div>
                </div>

                {/* Coluna 2 - Régua com marcador */}
                <div className="relative w-[480px]">
                  <div
                    className="absolute bottom-10 z-10"
                    style={{
                      left: `${
                        +data?.score?.score <= 50
                          ? 2
                          : +data?.score?.score > 900
                          ? 83
                          : +data?.score?.score / 10 - 9
                      }%`,
                    }}
                  >
                    <div className="bg-white border border-gray-300 px-3 py-1 rounded-full text-sm font-semibold shadow text-center min-w-[50px]">
                      {+data?.score?.score}
                    </div>
                  </div>

                  <img
                    src="/assets/score/regua/regua_2.png"
                    alt="Régua Score"
                    className="w-full rounded"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!hideAgrupados && (
        <>
          <div>
            <TitleStripe className="mb-5">Quadro Societário</TitleStripe>
            {data && !data?.socios?.mensagem ? (
              <QuadroSocietario
                socios={
                  Array.isArray(data?.socios?.socio)
                    ? data?.socios?.socio
                    : [...[], data?.socios?.socio]
                }
              />
            ) : (
              <NoOcurrence />
            )}
          </div>

          <div>
            <TitleStripe className="mb-5">Administradores</TitleStripe>
            {data && !data?.administradores?.mensagem ? (
              <Administrador
                administradores={
                  Array.isArray(data?.administradores?.administrador)
                    ? data?.administradores?.administrador
                    : [...[], data?.administradores?.administrador]
                }
              />
            ) : (
              <NoOcurrence />
            )}
          </div>

          <div>
            <TitleStripe className="mb-5">
              {" "}
              Participações dos Sócios e Administradores em Outras Empresas
            </TitleStripe>
            {!data?.participacoes_socios_adm_outras_empresas?.mensagem ? (
              <>
                {Array.isArray(
                  data?.participacoes_socios_adm_outras_empresas?.socio
                ) ? (
                  <>
                    {data?.participacoes_socios_adm_outras_empresas?.socio?.map(
                      (socioP, index) => (
                        <div key={index} className="mt-10">
                          <div className="font-semibold mb-1 text-blue-600 underline">
                            {socioP.nome_socio}
                          </div>

                          <div className="overflow-x-auto mt-3 mb-5 rounded-lg border border-gray-300">
                            <Table striped>
                              <Table.Head className="text-black">
                                <Table.HeadCell
                                  className="text-center py-2 px-1"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Nº
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="py-2"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Denominação Social
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="text-center py-2 px-1"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Data <br /> Entrada
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="text-center py-2 px-1"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Part. (%)
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="text-center py-2 px-1"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Situação
                                </Table.HeadCell>
                                <Table.HeadCell
                                  className="py-2"
                                  style={{ background: "#EBF5FF" }}
                                >
                                  Pendências
                                </Table.HeadCell>
                              </Table.Head>

                              <Table.Body className="divide-y">
                                {Array.isArray(socioP?.socios_participantes) ? (
                                  <>
                                    {socioP?.socios_participantes?.map(
                                      (socio, index) => (
                                        <Table.Row
                                          key={index}
                                          className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black"
                                        >
                                          <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                            {index + 1}
                                          </Table.Cell>
                                          <Table.Cell className=" py-1 w-96">
                                            <div>{socio.razao_social}</div>
                                            <div style={{ fontSize: "12px" }}>
                                              CNPJ: {socio.cnpj}
                                            </div>
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 px-1">
                                            {socio.entrada}
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 px-1 px-1">
                                            {socio.participacao}
                                          </Table.Cell>
                                          <Table.Cell className="text-center py-1 w-24">
                                            {socio.situacao_cnpj}
                                          </Table.Cell>
                                          <Table.Cell className="py-1 text-2xs text-black">
                                            {socio?.indicador_debito?.toUpperCase() ===
                                            "SIM" ? (
                                              <>
                                                {socio?.restricoes_documento
                                                  ?.cheques_sem_fundo
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Cheques Sem Fundos:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {
                                                        socio?.restricoes_documento
                                                          ?.cheques_sem_fundo
                                                          ?.quantidade
                                                      }{" "}
                                                      ocorrências
                                                    </span>
                                                  </div>
                                                )}

                                                {socio?.restricoes_documento
                                                  ?.protestos?.quantidade !==
                                                  "0" && (
                                                  <div className="uppercase">
                                                    Protestos:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socio?.restricoes_documento
                                                          ?.protestos?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socio?.restricoes_documento
                                                  ?.pendencias_restricoes
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Pendências:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socio?.restricoes_documento
                                                          ?.pendencias_restricoes
                                                          ?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socio?.restricoes_documento
                                                  ?.acoes_judiciais?.quantidade !==
                                                  "0" && (
                                                  <div className="uppercase">
                                                    Ações cíveis:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socio?.restricoes_documento
                                                          ?.acoes_judiciais?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}

                                                {socio?.restricoes_documento
                                                  ?.falencias_recuperacao_judicial
                                                  ?.quantidade !== "0" && (
                                                  <div className="uppercase">
                                                    Rec. Judicial/Falências:{" "}
                                                    <span className="text-red-500 font-semibold">
                                                      {formatToReal(
                                                        socio?.restricoes_documento
                                                          ?.falencias_recuperacao_judicial
                                                          ?.valor
                                                      )}
                                                    </span>
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <div className="text-blue-500">
                                                Não há débitos
                                              </div>
                                            )}
                                          </Table.Cell>
                                        </Table.Row>
                                      )
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black">
                                      <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                        {1}
                                      </Table.Cell>
                                      <Table.Cell className=" py-1 w-96">
                                        <div>
                                          {
                                            socioP?.socios_participantes
                                              ?.razao_social
                                          }
                                        </div>
                                        <div style={{ fontSize: "12px" }}>
                                          CNPJ: {socioP?.socios_participantes?.cnpj}
                                        </div>
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 px-1">
                                        {socioP?.socios_participantes?.entrada}
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 px-1 px-1">
                                        {socioP?.socios_participantes?.participacao}
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 w-24">
                                        {
                                          socioP?.socios_participantes
                                            ?.situacao_cnpj
                                        }
                                      </Table.Cell>
                                      <Table.Cell className="py-1 text-2xs text-black">
                                        {socioP?.socios_participantes?.indicador_debito?.toUpperCase() ===
                                        "SIM" ? (
                                          <>
                                            {socioP?.socios_participantes
                                              ?.restricoes_documento
                                              ?.cheques_sem_fundo?.quantidade !==
                                              "0" && (
                                              <div className="uppercase">
                                                Cheques Sem Fundos:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {
                                                    socioP?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.cheques_sem_fundo
                                                      ?.quantidade
                                                  }{" "}
                                                  ocorrências
                                                </span>
                                              </div>
                                            )}

                                            {socioP?.socios_participantes
                                              ?.restricoes_documento?.protestos
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Protestos:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socioP?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.protestos?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socioP?.socios_participantes
                                              ?.restricoes_documento
                                              ?.pendencias_restricoes
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Pendências:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socioP?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.pendencias_restricoes?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socioP?.socios_participantes
                                              ?.restricoes_documento
                                              ?.acoes_judiciais?.quantidade !==
                                              "0" && (
                                              <div className="uppercase">
                                                Ações cíveis:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socioP?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.acoes_judiciais?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socioP?.socios_participantes
                                              ?.restricoes_documento
                                              ?.falencias_recuperacao_judicial
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Rec. Judicial/Falências:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socioP?.socios_participantes
                                                      ?.restricoes_documento
                                                      ?.falencias_recuperacao_judicial
                                                      ?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="text-blue-500">
                                            Não há débitos
                                          </div>
                                        )}
                                      </Table.Cell>
                                    </Table.Row>
                                  </>
                                )}
                              </Table.Body>
                            </Table>
                          </div>
                        </div>
                      )
                    )}
                  </>
                ) : (
                  <>
                    <div className="mt-10">
                      <div className="font-semibold mb-1 text-blue-600 text-center underline">
                        {
                          data?.participacoes_socios_adm_outras_empresas?.socio
                            .nome_socio
                        }
                      </div>

                      <div className="overflow-x-auto mt-3 mb-5 rounded-lg border border-gray-300">
                        <Table striped>
                          <Table.Head className="text-black">
                            <Table.HeadCell
                              className="text-center py-2 px-1"
                              style={{ background: "#EBF5FF" }}
                            >
                              Nº
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="py-2"
                              style={{ background: "#EBF5FF" }}
                            >
                              Denominação Social
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="text-center py-2 px-1"
                              style={{ background: "#EBF5FF" }}
                            >
                              Data <br /> Entrada
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="text-center py-2 px-1"
                              style={{ background: "#EBF5FF" }}
                            >
                              Part. (%)
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="text-center py-2 px-1"
                              style={{ background: "#EBF5FF" }}
                            >
                              Situação
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="py-2"
                              style={{ background: "#EBF5FF" }}
                            >
                              Pendências
                            </Table.HeadCell>
                          </Table.Head>

                          <Table.Body className="divide-y">
                            {Array.isArray(
                              data?.participacoes_socios_adm_outras_empresas?.socio
                                ?.socios_participantes
                            ) ? (
                              <>
                                {data?.participacoes_socios_adm_outras_empresas?.socio?.socios_participantes?.map(
                                  (socio, index) => (
                                    <Table.Row
                                      key={index}
                                      className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black"
                                    >
                                      <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                        {index + 1}
                                      </Table.Cell>
                                      <Table.Cell className=" py-1 w-96">
                                        <div>{socio.razao_social}</div>
                                        <div style={{ fontSize: "12px" }}>
                                          CNPJ: {socio.cnpj}
                                        </div>
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 px-1">
                                        {socio.entrada}
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 px-1 px-1">
                                        {socio.participacao}
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 w-24">
                                        {socio.situacao_cnpj}
                                      </Table.Cell>
                                      <Table.Cell className="py-1 text-2xs text-black">
                                        {socio?.indicador_debito?.toUpperCase() ===
                                        "SIM" ? (
                                          <>
                                            {socio?.restricoes_documento
                                              ?.cheques_sem_fundo?.quantidade !==
                                              "0" && (
                                              <div className="uppercase">
                                                Cheques Sem Fundos:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {
                                                    socio?.restricoes_documento
                                                      ?.cheques_sem_fundo
                                                      ?.quantidade
                                                  }{" "}
                                                  ocorrências
                                                </span>
                                              </div>
                                            )}

                                            {socio?.restricoes_documento?.protestos
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Protestos:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socio?.restricoes_documento
                                                      ?.protestos?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socio?.restricoes_documento
                                              ?.pendencias_restricoes
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Pendências:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socio?.restricoes_documento
                                                      ?.pendencias_restricoes?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socio?.restricoes_documento
                                              ?.acoes_judiciais?.quantidade !==
                                              "0" && (
                                              <div className="uppercase">
                                                Ações cíveis:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socio?.restricoes_documento
                                                      ?.acoes_judiciais?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socio?.restricoes_documento
                                              ?.falencias_recuperacao_judicial
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Rec. Judicial/Falências:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socio?.restricoes_documento
                                                      ?.falencias_recuperacao_judicial
                                                      ?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="text-blue-500">
                                            Não há débitos
                                          </div>
                                        )}
                                      </Table.Cell>
                                    </Table.Row>
                                  )
                                )}
                              </>
                            ) : (
                              <>
                                <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black">
                                  <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                    {1}
                                  </Table.Cell>
                                  <Table.Cell className=" py-1 w-96">
                                    <div>
                                      {
                                        data
                                          ?.participacoes_socios_adm_outras_empresas
                                          ?.socio?.socios_participantes
                                          ?.razao_social
                                      }
                                    </div>
                                    <div style={{ fontSize: "10px" }}>
                                      CNPJ:{" "}
                                      {
                                        data
                                          ?.participacoes_socios_adm_outras_empresas
                                          ?.socio?.socios_participantes?.cnpj
                                      }
                                    </div>
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 px-1">
                                    {
                                      data?.participacoes_socios_adm_outras_empresas
                                        ?.socio?.socios_participantes?.entrada
                                    }
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 px-1 px-1">
                                    {
                                      data?.participacoes_socios_adm_outras_empresas
                                        ?.socio?.socios_participantes?.participacao
                                    }
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 w-24">
                                    {
                                      data?.participacoes_socios_adm_outras_empresas
                                        ?.socio?.socios_participantes?.situacao_cnpj
                                    }
                                  </Table.Cell>
                                  <Table.Cell className="py-1 text-2xs text-black">
                                    {data?.participacoes_socios_adm_outras_empresas?.socio?.socios_participantes.indicador_debito?.toUpperCase() ===
                                    "SIM" ? (
                                      <>
                                        {data
                                          ?.participacoes_socios_adm_outras_empresas
                                          ?.socio?.socios_participantes
                                          .restricoes_documento?.cheques_sem_fundo
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Cheques Sem Fundos:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {
                                                data
                                                  ?.participacoes_socios_adm_outras_empresas
                                                  ?.socio?.socios_participantes
                                                  .restricoes_documento
                                                  ?.cheques_sem_fundo?.quantidade
                                              }
                                            </span>
                                          </div>
                                        )}

                                        {data
                                          ?.participacoes_socios_adm_outras_empresas
                                          ?.socio?.socios_participantes
                                          .restricoes_documento?.protestos
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Protestos:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                data
                                                  ?.participacoes_socios_adm_outras_empresas
                                                  ?.socio?.socios_participantes
                                                  .restricoes_documento?.protestos
                                                  ?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {data
                                          ?.participacoes_socios_adm_outras_empresas
                                          ?.socio?.socios_participantes
                                          .restricoes_documento
                                          ?.pendencias_restricoes?.quantidade !==
                                          "0" && (
                                          <div className="uppercase">
                                            Pendências:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                data
                                                  ?.participacoes_socios_adm_outras_empresas
                                                  ?.socio?.socios_participantes
                                                  .restricoes_documento
                                                  ?.pendencias_restricoes?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {data
                                          ?.participacoes_socios_adm_outras_empresas
                                          ?.socio?.socios_participantes
                                          .restricoes_documento?.acoes_judiciais
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Ações cíveis:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                data
                                                  ?.participacoes_socios_adm_outras_empresas
                                                  ?.socio?.socios_participantes
                                                  .restricoes_documento
                                                  ?.acoes_judiciais?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {data
                                          ?.participacoes_socios_adm_outras_empresas
                                          ?.socio?.socios_participantes
                                          .restricoes_documento
                                          ?.falencias_recuperacao_judicial
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Rec. Judicial/Falências:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                data
                                                  ?.participacoes_socios_adm_outras_empresas
                                                  ?.socio?.socios_participantes
                                                  .restricoes_documento
                                                  ?.falencias_recuperacao_judicial
                                                  ?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="text-blue-500">
                                        Não há débitos
                                      </div>
                                    )}
                                  </Table.Cell>
                                </Table.Row>
                              </>
                            )}
                          </Table.Body>
                        </Table>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <NoOcurrence />
            )}
          </div>
        </>
      )}

      {!hideAgrupados && (
        <>
          <div>
            <TitleStripe className="mb-5">Cheques Sem Fundos</TitleStripe>
            {!!data?.cheques_sem_fundo?.total_cheques_sem_fundos ? (
              <>
                <ChequesSemFundos
                  quantidadeTotal={
                    data?.cheques_sem_fundo?.total_cheques_sem_fundos
                  }
                  totalNomes={data?.cheques_sem_fundo?.total_nomes_relacionados}
                  chequesSemFundos={
                    Array.isArray(
                      data?.cheques_sem_fundo?.ultimas_ocorrencias?.cheque_sem_fundo
                    )
                      ? data?.cheques_sem_fundo?.ultimas_ocorrencias
                          ?.cheque_sem_fundo
                      : [
                          ...[],
                          data?.cheques_sem_fundo?.ultimas_ocorrencias
                            ?.cheque_sem_fundo,
                        ]
                  }
                />
              </>
            ) : (
              <NoOcurrence />
            )}
          </div>
          <div>
            <TitleStripe className="mb-5">Cheques Sustados</TitleStripe>
            {data?.cheques_sustados?.ultimas_ocorrencias?.cheques_sustado?.length >
            0 ? (
              <>
                <ChequesSustados
                  periodoInicial={data?.cheques_sustados?.periodo_inicial}
                  periodoFinal={data?.cheques_sustados?.periodo_final}
                  quantidadeTotal={data?.cheques_sustados?.total}
                  chequesSustados={
                    Array.isArray(
                      data?.cheques_sustados?.ultimas_ocorrencias?.cheques_sustado
                    )
                      ? data?.cheques_sustados?.ultimas_ocorrencias?.cheques_sustado
                      : [
                          ...[],
                          data?.cheques_sustados?.ultimas_ocorrencias
                            ?.cheques_sustado,
                        ]
                  }
                />
              </>
            ) : (
              <NoOcurrence />
            )}
          </div>

          <div>
            <TitleStripe className="mb-5">Protestos</TitleStripe>
            {!!data?.protestos?.total_protestos ? (
              <Protestos
                quantidadeTotal={data?.protestos?.total_protestos}
                valorTotal={data?.protestos?.valor_total}
                primeiroValor={data?.protestos?.valor_primeiro}
                protestos={
                  Array.isArray(data?.protestos?.ultimas_ocorrencias?.protesto)
                    ? data?.protestos?.ultimas_ocorrencias?.protesto
                    : [...[], data?.protestos?.ultimas_ocorrencias?.protesto]
                }
                temVencimento={false}
              />
            ) : (
              <NoOcurrence />
            )}
          </div>

          <div>
            <TitleStripe className="mb-5">Negativações Registradas</TitleStripe>
            {data?.pendencias_restricoes?.ultimas_ocorrencias
              ?.pendencia_restricao ? (
              <PendenciasFinanceiras
                quantidadeTotal={data?.pendencias_restricoes?.total_pendencias}
                dataMaiorPendencia={data?.pendencias_restricoes?.data_maior}
                valorTotal={data?.pendencias_restricoes?.valor}
                pendenciasFinanceiras={(Array.isArray(
                  data?.pendencias_restricoes?.ultimas_ocorrencias
                    ?.pendencia_restricao
                )
                  ? data?.pendencias_restricoes?.ultimas_ocorrencias
                      ?.pendencia_restricao
                  : [
                      data?.pendencias_restricoes?.ultimas_ocorrencias
                        ?.pendencia_restricao,
                    ]
                ).map((item) => ({
                  data: item?.data_debito || null,
                  valor: item?.valor || 0,
                  informante: item?.informante || "",
                  tipo: item?.tipo || "",
                  documento: item?.documento || "",
                  situacao: item?.situacao || "",
                  uf: item?.uf || "",
                  cidade: item?.cidade || "",
                }))}
              />
            ) : (
              <NoOcurrence />
            )}
          </div>

          <div>
            <TitleStripe className="mb-5">Ações Cíveis</TitleStripe>
            {!!data?.acoes_civeis?.total_acoes ? (
              <>
                <AcoesCiveis
                  quantidadeTotal={data?.acoes_civeis?.total_acoes}
                  primeiroValor={data?.acoes_civeis?.valor_primeiro}
                  valorTotal={data?.acoes_civeis?.valor_total}
                  acoesCiveis={
                    Array.isArray(
                      data?.acoes_civeis?.ultimas_ocorrencias?.acao_civel
                    )
                      ? data?.acoes_civeis?.ultimas_ocorrencias?.acao_civel
                      : [...[], data?.acoes_civeis?.ultimas_ocorrencias?.acao_civel]
                  }
                />
              </>
            ) : (
              <NoOcurrence />
            )}
          </div>

          <div>
            <TitleStripe className="mb-5">
              Falências/Recuperações Judiciais
            </TitleStripe>
            {data && !data?.falencias_recuperacao_judicial?.mensagem ? (
              <FalenciaRecuperacaoJudicial
                falenciaRecuperacaoJudicial={
                  Array.isArray(
                    data?.falencias_recuperacao_judicial.ultimas_ocorrencias
                      ?.falencia_recuperacao_judicial
                  )
                    ? data?.falencias_recuperacao_judicial.ultimas_ocorrencias
                        ?.falencia_recuperacao_judicial
                    : [
                        ...[],
                        data?.falencias_recuperacao_judicial.ultimas_ocorrencias
                          ?.falencia_recuperacao_judicial,
                      ]
                }
              />
            ) : (
              <NoOcurrence />
            )}
          </div>
        </>
      )}

      <div>
        <TitleStripe className="mb-5">Crédito Obtido</TitleStripe>
        {data?.credito_obtido ? (
          <>
            <div className="mt-4 font-semibold">
              Pontuação:{" "}
              <Badge className="w-fit inline" color="gray">
                {formatToReal(data?.credito_obtido?.pontuacao * 1000)}
              </Badge>
            </div>
            <div className="mt-2 w-full rounded-lg border border-gray-300 overflow-auto">
              <Table>
                <Table.Head>
                  {data?.credito_obtido?.lista_indicador_comportamento?.item_indicador?.map(
                    (credito, index) => (
                      <Table.HeadCell
                        key={index}
                        className="text-center text-black text-xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatDateToYY(credito.mes_ano)}
                      </Table.HeadCell>
                    )
                  )}
                </Table.Head>
                <Table.Body className="divide-y">
                  {data?.credito_obtido?.lista_indicador_comportamento?.item_indicador?.map(
                    (credito, index) => (
                      <Table.Cell
                        key={index}
                        className="text-center text-black text-xs px-1"
                      >
                        {formatToReal(credito.pontuacao_mensal * 1000) || "-"}
                      </Table.Cell>
                    )
                  )}
                </Table.Body>
              </Table>
            </div>
            <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
              Descrição: Valor total de crédito já concedido ao consumidor ou
              empresa pelas instituições consultadas. Representa o quanto foi
              efetivamente aprovado em financiamentos, compras parceladas ou
              outras operações de crédito.
            </div>
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Pagamento Pontual</TitleStripe>
        {data?.pagamento_pontual ? (
          <>
            <div className="mt-4 font-semibold">
              Pontuação:{" "}
              <Badge className="w-fit inline" color="gray">
                {data?.pagamento_pontual?.pontuacao}
              </Badge>
            </div>
            <div>
              <div className="border border-gray-300 rounded-lg mt-2">
                <Table>
                  <Table.Head>
                    {data?.pagamento_pontual?.lista_indicador_comportamento?.item_indicador?.map(
                      (credito, index) => (
                        <Table.HeadCell
                          key={index}
                          className="text-center text-black text-xs px-1"
                          style={{ background: "#EBF5FF" }}
                        >
                          {formatDateToYY(credito.mes_ano)}
                        </Table.HeadCell>
                      )
                    )}
                  </Table.Head>
                  <Table.Body className="divide-y">
                    {data?.pagamento_pontual?.lista_indicador_comportamento?.item_indicador?.map(
                      (credito, index) => {
                        const valor = parseFloat(credito?.pontuacao_mensal);
                        return (
                          <Table.Cell
                            key={index}
                            className="text-center text-black text-2xs px-1"
                          >
                            {!isNaN(valor) ? `${valor}%` : "–"}
                          </Table.Cell>
                        );
                      }
                    )}
                  </Table.Body>
                </Table>
              </div>
              <div className="flex justify-center items-center mt-3">
                <span className="text-green-500 font-semibold">100%</span> :
                Pagamentos Pontuais |{" "}
                <span className="text-red-500 font-semibold ml-1">0%</span>:
                Pagamentos não pontuais
              </div>
            </div>
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Percentual em atrasos</TitleStripe>

        {data?.pagamento_atrasado ? (
          <>
            <div className="w-full rounded-lg border border-gray-300 overflow-x-auto">
              <Table>
                <Table.Head>
                  <Table.HeadCell
                    className="text-left text-black text-2xs px-1"
                    style={{ background: "#EBF5FF" }}
                  >
                    Tipo
                  </Table.HeadCell>
                  {Object.values(
                    data.pagamento_atrasado
                  )[0]?.lista_indicador_comportamento?.item_indicador?.map(
                    (item, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center text-black text-2xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {item.mes_ano}
                      </Table.HeadCell>
                    )
                  )}
                </Table.Head>

                <Table.Body className="divide-y">
                  {Object.entries(data.pagamento_atrasado).map(
                    ([sectionKey, sectionValue], i) => {
                      const indicadores =
                        sectionValue?.lista_indicador_comportamento
                          ?.item_indicador;

                      if (!Array.isArray(indicadores)) return null;

                      return (
                        <Table.Row key={i}>
                          <Table.Cell className="text-left text-black text-2xs font-medium px-1 capitalize font-semibold">
                            {sectionKey.replaceAll("_", " ")}
                          </Table.Cell>
                          {indicadores.map((item, idx) => {
                            const valor = parseFloat(item.pontuacao_mensal);
                            return (
                              <Table.Cell
                                key={idx}
                                className="text-center text-black text-2xs px-1"
                              >
                                {!isNaN(valor) ? `${valor}%` : "–"}
                              </Table.Cell>
                            );
                          })}
                        </Table.Row>
                      );
                    }
                  )}
                </Table.Body>
              </Table>
            </div>
            <div className="flex justify-center items-center mt-3">
              <span className="text-red-500 font-semibold">100%</span> :
              Pagamentos em atrasos |{" "}
              <span className="text-green-500 font-semibold ml-1">0%</span> :
              Pagamentos em dia
            </div>
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">
          Compromissos (quantidade de títulos)
        </TitleStripe>
        {data?.compromissos ? (
          <>
            <div className="border border-gray-300 rounded-lg mt-2 overflow-x-auto">
              <Table>
                <Table.Head>
                  {data?.compromissos?.lista_indicador_comportamento?.item_indicador?.map(
                    (credito, index) => (
                      <Table.HeadCell
                        key={index}
                        className="text-center text-black text-2xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatDateToYY(credito.mes_ano)}
                      </Table.HeadCell>
                    )
                  )}
                </Table.Head>

                <Table.Body className="divide-y">
                  <Table.Row>
                    {data?.compromissos?.lista_indicador_comportamento?.item_indicador?.map(
                      (credito, index) => {
                        const valor = parseFloat(credito.pontuacao_mensal);
                        return (
                          <Table.Cell
                            key={index}
                            className="text-center text-black text-2xs px-1"
                          >
                            {!isNaN(valor) ? valor * 100 : "–"}
                          </Table.Cell>
                        );
                      }
                    )}
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
            <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
              Descrição: Número total de obrigações financeiras registradas no
              nome do consumidor ou empresa, como boletos, duplicatas, carnês ou
              contratos de crédito. Indica a quantidade de compromissos ativos
              assumidos, independentemente do valor.
            </div>
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Comprometimento Futuro</TitleStripe>
        {data?.comprometimento_futuro ? (
          <>
            <div className="font-semibold mt-2">Compromissos</div>

            <div className="border border-gray-300 rounded-lg mt-2 overflow-x-auto">
              <Table>
                <Table.Head>
                  {data?.comprometimento_futuro?.lista_compromissos?.compromisso?.map(
                    (credito, index) => (
                      <Table.HeadCell
                        key={index}
                        className="text-center text-black text-2xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatDateToYY(credito.periodo)}
                      </Table.HeadCell>
                    )
                  )}
                </Table.Head>

                <Table.Body className="divide-y">
                  <Table.Row>
                    {data?.comprometimento_futuro?.lista_compromissos?.compromisso?.map(
                      (credito, index) => {
                        const valor = parseFloat(credito.pontuacao);
                        return (
                          <Table.Cell
                            key={index}
                            className="text-center text-black text-2xs px-1"
                          >
                            {!isNaN(valor) ? valor * 100 : "–"}
                          </Table.Cell>
                        );
                      }
                    )}
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>

            <div className="font-semibold mt-5">Crédito</div>
            <div className="border border-gray-300 rounded-lg mt-2 overflow-x-auto">
              <Table>
                <Table.Head>
                  {data?.comprometimento_futuro?.lista_credito?.credito?.map(
                    (credito, index) => (
                      <Table.HeadCell
                        key={index}
                        className="text-center text-black text-2xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatDateToYY(credito.periodo)}
                      </Table.HeadCell>
                    )
                  )}
                </Table.Head>

                <Table.Body className="divide-y">
                  <Table.Row>
                    {data?.comprometimento_futuro?.lista_credito?.credito?.map(
                      (credito, index) => {
                        const valor = parseFloat(credito.pontuacao);
                        return (
                          <Table.Cell
                            key={index}
                            className="text-center text-black text-2xs px-1"
                          >
                            {!isNaN(valor) ? formatToReal(valor * 1000) : "–"}
                          </Table.Cell>
                        );
                      }
                    )}
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      {!hideAgrupados && (
        <>
          <div>
            <TitleStripe className="mb-5">Histórico de Consultas</TitleStripe>

            {data?.consultas?.ultimas_consultas?.consulta?.length > 0 ? (
              <>
                <HistoricoConsultas
                  quantidadeTotal={data?.consultas?.quantidade_total}
                  periodoInicial={data?.consultas?.periodo_inicial}
                  periodoFinal={data?.consultas?.periodo_final}
                  historicoConsultas={
                    Array.isArray(data?.consultas?.ultimas_consultas?.consulta)
                      ? data?.consultas?.ultimas_consultas?.consulta?.map(
                          (hist) => ({
                            data: hist.data,
                            empresa: hist.razao_social,
                          })
                        )
                      : [
                          ...[],
                          {
                            data: data?.consultas?.ultimas_consultas?.consulta
                              ?.data,
                            empresa:
                              data?.consultas?.ultimas_consultas?.consulta
                                ?.razao_social,
                          },
                        ]
                  }
                />
              </>
            ) : (
              <NoOcurrence />
            )}

            <div className="border border-gray-300 rounded-lg mt-3">
              <Table striped>
                <Table.Head>
                  {data?.consultas?.consultas_mes?.map((consulta, index) => (
                    <Table.HeadCell
                      key={index}
                      className="text-center text-black px-1"
                      style={{ background: "#EBF5FF" }}
                    >
                      {formatDateToDDMM(consulta.mes_ano)}
                    </Table.HeadCell>
                  ))}
                </Table.Head>

                <Table.Body className="divide-y">
                  <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800">
                    {data?.consultas?.consultas_mes?.map((consult, index) => (
                      <Table.Cell
                        className="font-medium text-gray-900 dark:text-white text-center text-black py-1 px-1"
                        key={index}
                      >
                        {consult.quantidade}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
          </div>
        </>
      )}
      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              <strong>Endereço:</strong> {fullAddress || "—"}
            </div>
            {fullAddress ? (
              <StreetView address={fullAddress} height={460} />
            ) : (
              <div className="p-3 text-sm text-gray-500">
                Endereço indisponível.
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DefineLimitePositivoQuery;
